import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/auth";
import { useMembersStore } from "@/lib/members";
import { useNavigate } from "@tanstack/react-router";
import { Bell, Bug, LogOut, User } from "lucide-react";
import { useTranslation } from "react-i18next";

interface UserMenuProps {
	compact?: boolean;
}

export function UserMenu({ compact }: UserMenuProps) {
	const { user, logout } = useAuthStore();
	const { members } = useMembersStore();
	const { t } = useTranslation();
	const navigate = useNavigate();

	if (!user) return null;

	const me = members.find((m) => m.email === user.email);

	const initials =
		user.name
			.split(/\s+/)
			.map((w) => w[0])
			.join("")
			.slice(0, 2)
			.toUpperCase() ||
		user.email[0]?.toUpperCase() ||
		"?";

	const avatarCircle = (size: string) =>
		me?.avatarUrl ? (
			<img
				src={me.avatarUrl}
				alt={user.name}
				className={`${size} shrink-0 rounded-full object-cover`}
			/>
		) : (
			<div
				className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground`}
			>
				{initials}
			</div>
		);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				{compact ? (
					<Button
						variant="ghost"
						size="icon"
						className="shrink-0 rounded-full h-auto w-auto p-0 hover:opacity-80"
					>
						{avatarCircle("h-7 w-7")}
					</Button>
				) : (
					<Button
						variant="ghost"
						className="flex w-full items-center gap-2 px-2 py-1.5 h-auto justify-start"
					>
						{avatarCircle("h-7 w-7")}
						<div className="flex flex-col items-start overflow-hidden">
							<span className="truncate text-sm font-medium leading-tight">
								{user.name}
							</span>
							<span className="truncate text-[11px] text-muted-foreground leading-tight">
								{user.email}
							</span>
						</div>
					</Button>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent
				side={compact ? "bottom" : "top"}
				align={compact ? "end" : "start"}
				className="w-56"
			>
				<DropdownMenuLabel className="font-normal">
					<div className="flex flex-col gap-0.5">
						<p className="text-sm font-medium leading-none">{user.name}</p>
						<p className="text-xs text-muted-foreground leading-none">
							{user.email}
						</p>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					onSelect={() => navigate({ to: "/settings/profile" })}
				>
					<User />
					{t("my_profile")}
				</DropdownMenuItem>
				<DropdownMenuItem
					onSelect={() => navigate({ to: "/settings/notifications" })}
				>
					<Bell />
					{t("notifications")}
				</DropdownMenuItem>
				<DropdownMenuItem
					onSelect={() => {
						window.dispatchEvent(new CustomEvent("omnios:report-problem"));
					}}
				>
					<Bug />
					{t("report_problem")}
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem onSelect={logout}>
					<LogOut />
					{t("logout")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
